import React, { useState, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useSEO } from './useSEO';

function safeNext(value) {
  if (!value || typeof value !== 'string') return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const labels = ['Too short', 'Weak', 'Okay', 'Good', 'Strong', 'Very strong'];
  return { score, label: labels[score] || '' };
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useSEO({
    title: 'Create account',
    description: 'Create your Student Resource Hub account to access GCSE and A-Level past papers, mark schemes, and topic questions.',
    canonicalPath: '/register'
  });

  const emailNorm = form.email.trim().toLowerCase();
  const emailValid = EMAIL_RE.test(emailNorm) && emailNorm.length <= 254;
  const pwInfo = useMemo(() => passwordStrength(form.password), [form.password]);
  const pwValid = form.password.length >= 8 && form.password.length <= 200;
  const formValid = emailValid && pwValid;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const onBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, password: true });
    if (!formValid) return;
    setLoading(true);
    setError(null);
    try {
      await register(form.name.trim(), emailNorm, form.password);
      navigate(next || '/timetable', { replace: true });
    } catch (err) {
      setError(err.message || 'We couldn’t create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const showEmailError = touched.email && form.email && !emailValid;
  const showPwError = touched.password && form.password && !pwValid;

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <h2>Create account</h2>
        <p className="auth-sub">Free forever. No card needed.</p>
        <form onSubmit={onSubmit} className="auth-form" noValidate>
          <label>
            Name <span className="auth-optional">(optional)</span>
            <input
              type="text"
              name="name"
              autoComplete="name"
              autoCapitalize="words"
              value={form.name}
              onChange={onChange}
              onBlur={onBlur}
              maxLength={120}
              placeholder="Your name"
            />
          </label>
          <label>
            Email
            <input
              type="email"
              name="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              inputMode="email"
              required
              value={form.email}
              onChange={onChange}
              onBlur={onBlur}
              maxLength={254}
              aria-invalid={showEmailError ? 'true' : 'false'}
              placeholder="you@example.com"
            />
            {showEmailError && <small className="auth-field-error">Please enter a valid email address.</small>}
          </label>
          <label>
            Password
            <div className="auth-pw-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="new-password"
                required
                minLength={8}
                maxLength={200}
                value={form.password}
                onChange={onChange}
                onBlur={onBlur}
                aria-invalid={showPwError ? 'true' : 'false'}
                aria-describedby="pw-help"
                placeholder="At least 8 characters"
              />
              <button
                type="button"
                className="auth-pw-toggle"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >{showPassword ? 'Hide' : 'Show'}</button>
            </div>
            {form.password && (
              <div className="auth-pw-meter" aria-hidden>
                <div className={`auth-pw-bar level-${pwInfo.score}`} />
                <span className="auth-pw-label">{pwInfo.label}</span>
              </div>
            )}
            <small id="pw-help" className={showPwError ? 'auth-field-error' : 'auth-hint'}>
              {showPwError ? 'Password must be at least 8 characters.' : 'Use 8 or more characters.'}
            </small>
          </label>
          {error && <p className="auth-error" role="alert">{error}</p>}
          <button type="submit" disabled={loading || !formValid} className="primary-btn auth-submit">
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <p className="auth-foot">
          Already have an account? <Link to={next ? `/login?next=${encodeURIComponent(next)}` : '/login'}>Log in</Link>
        </p>
      </div>
    </div>
  );
}
